/**
 * 全局用户设置：外观 / 视频 / 音频 / 房间默认值。
 * 轻量 localStorage store，不依赖房间；设置页、背景、媒体采集与建房弹窗共用。
 */
import { useSyncExternalStore } from 'react';

export type VideoFps = 15 | 24 | 30;

export interface AppSettings {
  /** 远端视频默认帧率（采集时作为 ideal/max 约束） */
  videoFps: VideoFps;
  /** 默认开启摄像头（仅默认值，不强制自动开） */
  cameraDefault: boolean;
  mirrorLocalVideo: boolean;
  /** 点阵背景 */
  backgroundDots: boolean;
  backgroundIntensity: number;
  /** 房间默认容量（信令层最终以 Worker 配置为准） */
  roomCapacity: 4 | 8 | 12;
  /** 新房间默认媒体策略 */
  mediaPolicy: 'audio-only' | 'audio-video';
  /** 记住的设备 */
  audioInputId: string;
  audioOutputId: string;
}

const KEY = 'zongheng.settings.v1';

export const DEFAULT_SETTINGS: AppSettings = {
  videoFps: 24,
  cameraDefault: false,
  mirrorLocalVideo: true,
  backgroundDots: true,
  backgroundIntensity: 1,
  roomCapacity: 8,
  mediaPolicy: 'audio-video',
  audioInputId: '',
  audioOutputId: '',
};

let settings: AppSettings = load();

function load(): AppSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<AppSettings>) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

const listeners = new Set<() => void>();

export function getSettings(): AppSettings {
  return settings;
}

export function updateSettings(patch: Partial<AppSettings>): void {
  settings = { ...settings, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
  for (const listener of listeners) listener();
}

export function resetSettings(): void {
  settings = { ...DEFAULT_SETTINGS };
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  for (const listener of listeners) listener();
}

export function subscribeSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSettings(): AppSettings {
  return useSyncExternalStore(subscribeSettings, getSettings, getSettings);
}
